import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  assistantService,
  ASSISTANT_QUOTA_ERROR,
  AssistantHistoryTurn,
  AssistantLanguage,
  AssistantUsage,
} from '../../services/assistantService';

/** A rendered chat message (user prompt or assistant reply). */
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

/** Last N turns of context sent back to the server for continuity. */
const HISTORY_TURNS = 8;
/** The 7 platform locales the assistant supports; anything else falls back to sk. */
const SUPPORTED: readonly AssistantLanguage[] = ['sk', 'en', 'cs', 'pl', 'de', 'hu', 'ro'];

const normalizeLanguage = (lang: string): AssistantLanguage =>
  (SUPPORTED as readonly string[]).includes(lang) ? (lang as AssistantLanguage) : 'sk';

let counter = 0;
const nextId = (): string => `m${Date.now()}-${counter++}`;

export interface UseAssistantChat {
  messages: ChatMessage[];
  usage: AssistantUsage | null;
  sending: boolean;
  /** i18n key of the last error, or null. */
  error: string | null;
  /** True once the monthly quota is exhausted — the UI shows the paywall stub. */
  quotaReached: boolean;
  send: (question: string) => Promise<void>;
}

/**
 * Conversation state for the assistant panel. Keeps the running message list,
 * the latest usage counter, and a quota/error flag. Builds the `history` context
 * (last ~8 turns) from prior messages and sends the current UI language so the
 * reply matches the user's locale.
 */
export function useAssistantChat(): UseAssistantChat {
  const { i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [usage, setUsage] = useState<AssistantUsage | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaReached, setQuotaReached] = useState(false);

  const send = useCallback(
    async (raw: string) => {
      const question = (raw || '').trim();
      if (!question || sending) return;

      // History = the conversation so far (before this question), last N turns.
      const history: AssistantHistoryTurn[] = messages
        .slice(-HISTORY_TURNS)
        .map((m) => ({ role: m.role, text: m.text }));

      const userMessage: ChatMessage = { id: nextId(), role: 'user', text: question };
      setMessages((prev) => [...prev, userMessage]);
      setSending(true);
      setError(null);

      try {
        const result = await assistantService.ask(
          question,
          normalizeLanguage(i18n.language),
          history,
        );
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'assistant', text: result.answer },
        ]);
        setUsage(result.usage);
        if (result.usage.used >= result.usage.limit) {
          setQuotaReached(true);
        }
      } catch (err) {
        const key = err instanceof Error ? err.message : 'assistant.errors.failed';
        setError(key);
        if (key === ASSISTANT_QUOTA_ERROR) {
          setQuotaReached(true);
        }
      } finally {
        setSending(false);
      }
    },
    [messages, sending, i18n.language],
  );

  return { messages, usage, sending, error, quotaReached, send };
}
